import { getLogger } from '@hashnode/file-parsing'
import assert from 'assert'
import prompts from 'prompts'
import { createHeaders, getClient } from '../graphql/get-client.js'
import { AbortCommandError } from '../utils/errors.js'
import { getUserSessionStore, SessionData, UserSettingsData } from './user-store.js'

const MAX_RETRIES = 3

const logger = getLogger()

/** Show login prompt while prompting for missing credentials. */
export async function showLoginPromptAsync({
	printNewLine = false,
}: {
	printNewLine?: boolean
}): Promise<void> {
	if (printNewLine) {
		logger.log('')
	}

	const { variant } = await prompts({
		type: 'select',
		name: 'variant',
		message: 'Choose a login method',
		choices: [
			{
				title: 'Personal Access Token (PAT)',
				description: 'Login with a Personal Access Token',
				selected: true,
				value: 'pat',
			},
		],
	})

	if (variant === 'pat') {
		let sessionData: UserSettingsData['auth']

		let retry = 0
		while (retry < MAX_RETRIES) {
			const { pat } = await prompts({
				name: 'pat',
				type: 'text',
				message: `Enter your personal access token, which will be used to authenticate you.`,
			})

			try {
				const queryHeaders = createHeaders({ pat })

				const sdk = getClient(queryHeaders)

				const meData = await sdk.CheckLogin()

				logger.success('Success - You are now logged in.')
				retry = 3

				sessionData = {
					currentConnection: 'Personal-Access-Token',
					sessionSecret: pat,
					userId: meData.me.id,
					username: meData.me.username,
				}

				getUserSessionStore().set('auth', sessionData)
			} catch {
				logger.error('Invalid personal access token')
			} finally {
				retry++
			}
		}

		if (retry > 3 && !sessionData) {
			logger.exit('Failed to login after 3 attempts. Exiting...')
		}
	} else {
		throw new AbortCommandError()
	}
}

/** Ensure the user is logged in, if not, prompt to login. */
export async function ensureLoggedInAsync(): Promise<SessionData> {
	let authSession = await getUserSessionStore().get('auth')
	if (!authSession || !authSession.sessionSecret) {
		logger.warn('You need to login to continue.')
		await showLoginPromptAsync({ printNewLine: true })

		authSession = await getUserSessionStore().get('auth')
		assert(authSession, 'User should be logged in')
	}

	return authSession
}
